import React, { useState, useEffect } from 'react';
import { X, User, Phone, MapPin, Lock, Camera, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatUzPhone, capitalizeWords, UZBEKISTAN_REGIONS } from '../../utils/helpers';
import { showToast } from '../common/Toast';
import PasswordInput from '../common/PasswordInput';
import { api } from '../../api/client';

export default function ProfileModal({ isOpen, onClose }) {
  const { user, updateProfile } = useAuth();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [region, setRegion] = useState('Jizzax viloyati');
  const [district, setDistrict] = useState('Jizzax shahar');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [password, setPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFullName(user.fullName || '');
      setPhone(user.phone ? formatUzPhone(user.phone) : '+998 ()');
      setRegion(user.region || 'Jizzax viloyati');
      setDistrict(user.district || 'Jizzax shahar');
      setEmail(user.email || '');
      setAvatar(user.avatar || '');
      setPassword('');
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Instant local preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setAvatar(event.target.result);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);

    try {
      const res = await api.postFormData('/upload', formData);
      if (res?.success && res?.url) {
        setAvatar(res.url);
        showToast('Rasm muvaffaqiyatli yuklandi!', 'success');
      }
    } catch (err) {
      showToast('Rasm tanlandi', 'info');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({
        fullName: capitalizeWords(fullName.trim()),
        phone: phone.trim(),
        region,
        district,
        email: email.trim() || undefined,
        avatar,
        password: password.trim() ? password.trim() : undefined,
      });
      showToast('Profil muvaffaqiyatli yangilandi!', 'success');
      onClose();
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setSaving(false);
    }
  };

  const districtsList = UZBEKISTAN_REGIONS[region] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg bg-[#070c16] border border-[#c59b27]/40 rounded-2xl shadow-2xl shadow-black/90 overflow-hidden z-10 my-6 animate-in zoom-in-95 duration-200">
        <div className="h-1 w-full bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#d4af37]" />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-[#111c2e] hover:bg-[#1a2942] border border-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="text-center mb-6">
            {/* Avatar Preview & Direct Photo Upload on Click */}
            <div className="relative w-20 h-20 mx-auto mb-3">
              <label 
                className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#c59b27] bg-[#111c2e] flex items-center justify-center shadow-lg shadow-black/80 cursor-pointer group relative block"
                title="Fotosuratni almashtirish uchun bosing"
              >
                {avatar ? (
                  <img src={avatar} alt={fullName} className="w-full h-full object-cover group-hover:opacity-70 transition-opacity" />
                ) : (
                  <User className="w-10 h-10 text-slate-400 group-hover:text-white transition-colors" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera className="w-6 h-6 text-[#c59b27]" />
                </div>
                <input type="file" accept="image/*" onChange={handleAvatarFile} className="hidden" />
              </label>
              <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-[#c59b27] text-black flex items-center justify-center pointer-events-none shadow-md">
                <Camera className="w-3 h-3" />
              </div>
            </div>
            {uploading && <p className="text-[11px] text-[#c59b27] mb-1">Rasm yuklanmoqda...</p>}

            <h3 className="font-serif text-2xl font-bold text-slate-100">
              Mening Profilim
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Shaxsiy ma’lumotlaringizni tahrirlashingiz va parolni o‘zgartirishingiz mumkin
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                Ism va Familiya
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                  <User className="w-3.5 h-3.5" />
                </div>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(capitalizeWords(e.target.value))}
                  className="w-full pl-9 pr-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27]"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                Telefon Raqam
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[#c59b27]">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <input
                  type="tel"
                  required
                  placeholder="+998 (97) 325-07-70"
                  value={phone || '+998 ()'}
                  onFocus={() => {
                    if (!phone || phone === '') setPhone('+998 ()');
                  }}
                  onChange={(e) => setPhone(formatUzPhone(e.target.value, phone))}
                  className="w-full pl-9 pr-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded text-xs text-slate-100 font-mono focus:outline-none focus:border-[#c59b27]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                  Viloyat
                </label>
                <select
                  value={region}
                  onChange={(e) => {
                    const r = e.target.value;
                    setRegion(r);
                    const d = UZBEKISTAN_REGIONS[r] || [];
                    setDistrict(d[0] || '');
                  }}
                  className="w-full px-3 py-2 bg-[#0b1220] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27]"
                >
                  {Object.keys(UZBEKISTAN_REGIONS).map((reg) => (
                    <option key={reg} value={reg} className="bg-[#0b1220] text-slate-200">
                      {reg}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                  Tuman / Shahar
                </label>
                <select
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  className="w-full px-3 py-2 bg-[#0b1220] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27]"
                >
                  {districtsList.map((d) => (
                    <option key={d} value={d} className="bg-[#0b1220] text-slate-200">
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                Yangi Parol (O‘zgartirmoqchi bo‘lsangiz)
              </label>
              <PasswordInput
                placeholder="Bo‘sh qoldirilsa, eski parol qoladi"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                startIcon={<Lock className="w-3.5 h-3.5" />}
                className="w-full py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27]"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full mt-4 py-3 px-4 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/30 transition-all flex items-center justify-center cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saqlanmoqda...
                </>
              ) : (
                'O‘ZGARISHLARNI SAQLASH'
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
